import { useState } from "react";
import { useCanAccess, useGetList, useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

import { useIsMobile } from "@/hooks/use-mobile";
import { LanguageToggle } from "../layout/LanguageToggle";
import type { Sale } from "../types";
import { formatUsd, type Shipment } from "../shipments/types";
import { ChannelStats } from "./ChannelStats";

// Connor's agreed share of total sales. Shown to administrators only.
const OWNER_SHARE_RATE = 0.05;
const OWNER_SHARE_LABEL = "egi.performance.owner_share";

const monthStart = (offset = 0) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return d.toISOString().slice(0, 7);
};
// Local midnight on the 1st of the month, `offset` months after `ym`
const monthIso = (ym: string, offset = 0) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1 + offset, 1).toISOString();
};
const lastDay = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m, 0).getDate();
};

export const PerformancePage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const [from, setFrom] = useState(monthStart(-2));
  const [to, setTo] = useState(monthStart());

  // ponytail: loads every shipment in the range in one request; fine for a few thousand rows a year.
  // Move the aggregation into a SQL view once a range holds more than ~10k shipments.
  const { data: shipments = [], isPending } = useGetList<Shipment>(
    "shipments",
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "invoice_date", order: "ASC" },
      filter: {
        "invoice_date@gte": `${from}-01`,
        "invoice_date@lte": `${to}-${lastDay(to)}`,
        status: "active",
      },
    },
  );
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 200 },
  });
  const name = (id: unknown) => {
    const s = sales.find((x) => x.id === id);
    return s ? `${s.first_name} ${s.last_name}` : `#${id}`;
  };

  const months = [...new Set(shipments.map((s) => s.invoice_date.slice(0, 7)))];
  const people = [...new Set(shipments.map((s) => s.sales_id))];
  const cell = (month: string | null, person: unknown | null) =>
    shipments
      .filter(
        (s) =>
          (month === null || s.invoice_date.startsWith(month)) &&
          (person === null || s.sales_id === person),
      )
      .reduce((acc, s) => acc + Number(s.total), 0);
  const count = (person: unknown) =>
    shipments.filter((s) => s.sales_id === person).length;
  const grand = cell(null, null);
  // Intercompany (parent/subsidiary) trades count for the salesperson but not for the owner's share.
  const shareBase = (month: string | null) =>
    shipments
      .filter(
        (s) =>
          s.trade_type !== "intercompany" &&
          (month === null || s.invoice_date.startsWith(month)),
      )
      .reduce((acc, s) => acc + Number(s.total), 0);
  const intercompanyTotal = grand - shareBase(null);

  return (
    <div className="mt-2 flex flex-col gap-4 px-4 md:px-0">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="text-2xl font-semibold mr-auto">
          {translate("egi.performance.title")}
        </h1>
        {isMobile ? <LanguageToggle /> : null}
        <label className="text-sm">
          {translate("egi.performance.from")}
          <Input
            type="month"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
        <label className="text-sm">
          {translate("egi.performance.to")}
          <Input
            type="month"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
          />
        </label>
      </div>
      <p className="text-sm text-muted-foreground">
        {translate("egi.performance.note")}
        {isAdmin ? "" : ` ${translate("egi.performance.own_only")}`}
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat
          label={translate("egi.performance.total_sales")}
          value={formatUsd(grand)}
        />
        <Stat
          label={translate("egi.performance.shipments")}
          value={String(shipments.length)}
        />
        {isAdmin ? (
          <Stat
            label={translate(OWNER_SHARE_LABEL)}
            value={formatUsd(shareBase(null) * OWNER_SHARE_RATE)}
            hint={
              intercompanyTotal > 0
                ? translate("egi.performance.share_excludes", {
                    amount: formatUsd(intercompanyTotal),
                  })
                : undefined
            }
            accent
          />
        ) : null}
      </div>

      <Card>
        <CardContent className="overflow-x-auto">
          {isPending ? (
            <div className="text-sm text-muted-foreground">
              {translate("egi.performance.loading")}
            </div>
          ) : shipments.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              {translate("egi.performance.empty")}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-foreground text-left">
                  <th className="py-2 pr-4 font-semibold">
                    {translate("egi.fields.salesperson")}
                  </th>
                  {months.map((m) => (
                    <th key={m} className="py-2 pr-4 font-semibold text-right">
                      {m}
                    </th>
                  ))}
                  <th className="py-2 pr-4 font-semibold text-right">
                    {translate("egi.performance.shipments")}
                  </th>
                  <th className="py-2 font-semibold text-right">
                    {translate("egi.fields.total")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <tr key={String(p)} className="border-b">
                    <td className="py-2 pr-4">{name(p)}</td>
                    {months.map((m) => (
                      <td key={m} className="py-2 pr-4 text-right tabular-nums">
                        {formatUsd(cell(m, p))}
                      </td>
                    ))}
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {count(p)}
                    </td>
                    <td className="py-2 text-right tabular-nums font-medium">
                      {formatUsd(cell(null, p))}
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="py-2 pr-4">
                    {translate("egi.performance.all")}
                  </td>
                  {months.map((m) => (
                    <td key={m} className="py-2 pr-4 text-right tabular-nums">
                      {formatUsd(cell(m, null))}
                    </td>
                  ))}
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {shipments.length}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {formatUsd(grand)}
                  </td>
                </tr>
                {isAdmin ? (
                  <tr className="text-[#FF6A00]">
                    <td className="py-2 pr-4">
                      {translate(OWNER_SHARE_LABEL)}
                    </td>
                    {months.map((m) => (
                      <td key={m} className="py-2 pr-4 text-right tabular-nums">
                        {formatUsd(shareBase(m) * OWNER_SHARE_RATE)}
                      </td>
                    ))}
                    <td />
                    <td className="py-2 text-right tabular-nums">
                      {formatUsd(shareBase(null) * OWNER_SHARE_RATE)}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {isPending ? null : (
        <ChannelStats
          fromIso={monthIso(from)}
          toIso={monthIso(to, 1)}
          shipments={shipments}
        />
      )}
    </div>
  );
};

PerformancePage.path = "/performance";

const Stat = ({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: boolean;
}) => (
  <Card>
    <CardContent>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        className={`text-2xl font-semibold tabular-nums ${accent ? "text-[#FF6A00]" : ""}`}
      >
        {value}
      </div>
      {hint ? (
        <div className="text-xs text-muted-foreground mt-1">{hint}</div>
      ) : null}
    </CardContent>
  </Card>
);
